// @flow

import * as React from 'react';
import { Box } from 'react-system';
import { Button, Select } from '@material-ui/core';
import { useTheme } from '../../../theme';
import { Filter } from '../../../controls/Filter';
import { useLocale } from '../../../hooks/locale';
import { createDescriptor } from '../../../controls/router';
import type { AggregatesListingsFilters_root } from '../__generated__/AggregatesListingsFilters_root.graphql.js';
import {
  pageDescriptor,
  propertyTypeFilterDescriptor,
  offerTypeFilterDescriptor,
} from './sharedDescriptors';
import {
  propertyTypeFilterChoicesBuy,
  propertyTypeFilterChoicesRent,
} from './propertyTypeFilterChoices';
import {
  getPropertyTypesAllDescriptors,
  paramsToReset,
} from './filtersByPropertyMainType';
export {
  propertyTypeFilterChoicesBuy,
  propertyTypeFilterChoicesRent,
} from './propertyTypeFilterChoices';
export {
  propertyTypeSEOChoicesBuy,
  propertyTypeSEOChoicesRent,
} from './propertyTypeFilterChoices';

const combinedDescriptor = createDescriptor(
  propertyTypeFilterDescriptor,
  offerTypeFilterDescriptor,
  pageDescriptor,
);

type Props = {|
  root: AggregatesListingsFilters_root,
  inline?: boolean,
  shouldFlush: boolean,
|};

export const PropertyMainTypesFilter = (props: Props): React.Node => {
  const [
    descriptor,
    setDescriptor,
    flushDescriptor,
  ] = combinedDescriptor.useState();
  const filtersDescriptor = createDescriptor(
    ...getPropertyTypesAllDescriptors(),
  );
  const [, setFiltersParams] = filtersDescriptor.useState();
  const { text } = useTheme();
  const empty = propertyTypeFilterDescriptor.useIsEmpty();
  const { t } = useLocale();
  const flush = () => {
    setDescriptor({ page: 0 });
    if (props.shouldFlush) {
      flushDescriptor();
    }
  };

  const dialogTitle = t('propertyTypeFilter');

  const isChecked = ({ value }) => {
    return descriptor.compositePropertyType_eq === value;
  };

  const onClick = value => {
    setDescriptor({ compositePropertyType_eq: value });
    setFiltersParams(paramsToReset(value));
    flush();
  };

  const COMBINE_FIRST = 2;

  let checkboxes;
  if (descriptor.offerType === 'buy') {
    checkboxes = propertyTypeFilterChoicesBuy(t);
  } else {
    checkboxes = propertyTypeFilterChoicesRent(t);
  }

  const checkedValues = checkboxes.filter(isChecked);

  let label = empty
    ? dialogTitle
    : checkedValues
        .slice(0, COMBINE_FIRST)
        .map(v => v.title)
        .join(', ');

  if (checkedValues.length > COMBINE_FIRST) {
    label += ` +${checkedValues.length - COMBINE_FIRST}`;
  }

  return props.inline === true ? (
    <Select
      variant="outlined"
      native={true}
      value={descriptor.compositePropertyType_eq || ''}
      onChange={event =>
        onClick(event.target.value === 'ANY' ? null : (event.target.value: any))
      }
    >
      <option value="ANY">{t('any')}</option>
      {checkboxes.map(ch => (
        <option key={ch.title} value={ch.value}>
          {ch.title}
        </option>
      ))}
    </Select>
  ) : (
    <Filter
      dialogTitle={
        <Box p={3} pb={0} css={text.subtitle1}>
          {dialogTitle}
        </Box>
      }
      label={label}
      empty={empty}
      onReset={() => {
        setDescriptor(propertyTypeFilterDescriptor.defaults);
        flush();
      }}
    >
      <Box p={3}>
        <div
          css={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
            gridGap: '12px 16px',
          }}
        >
          {checkboxes.map(ch => (
            <Button
              key={ch.title}
              css={[
                isChecked(ch) ? { border: '1px solid rgba(0, 0, 0, 0)' } : null,
                {
                  padding: 8,
                  fontWeight: 600,
                  '.MuiButton-label': [text.ellipsis, { display: 'block' }],
                },
              ]}
              onClick={() => {
                onClick(ch.value);
              }}
              variant={isChecked(ch) ? 'contained' : 'outlined'}
              color={isChecked(ch) ? 'primary' : 'inherit'}
            >
              {ch.title}
            </Button>
          ))}
        </div>
      </Box>
    </Filter>
  );
};
